import PropTypes from 'prop-types'
import React, { Component, Fragment } from 'react'
import classNames from 'classnames'
import { connect } from 'react-redux'
import { Trash, X } from '@everlane/icon'

import Hub from 'base/hub'
import Event from 'base/event'
import { closeCart } from 'base/actions/synchronous'
import { updateGiftcard } from 'base/actions/asynchronous'
import { getSneakersGenderedSizeName } from 'base/lib/product'
import ImageHelper from 'base/lib/image_helper'
import routeTo from 'base/lib/route_to'
import LineItemNotifications from 'base/components/checkout/line_item_notifications'
import GiftcardModal from 'desktop/components/products/giftcard_modal'

class LineItem extends Component {
  static propTypes = {
    isFlyoutCart: PropTypes.bool,
    imageHeight: PropTypes.number.isRequired,
    onIncrementQuantity: PropTypes.func,
    onDecrementQuantity: PropTypes.func,
    onUpdateGiftcard: PropTypes.func,
    onRemove: PropTypes.func,
    isSyncing: PropTypes.bool,
    lineItem: PropTypes.shape({
      id: PropTypes.number.isRequired,
      image_url: PropTypes.string.isRequired,
      size: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      sku: PropTypes.string.isRequired,
      total: PropTypes.string.isRequired,
      original_total: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      is_preorder: PropTypes.bool.isRequired,
      is_promo: PropTypes.bool.isRequired,
      is_virtual: PropTypes.bool.isRequired,
      product_id: PropTypes.number,
      product_permalink: PropTypes.string,
      collection_permalink: PropTypes.string,
      is_digital_giftcard: PropTypes.bool.isRequired,
      is_giftcard: PropTypes.bool.isRequired,
    }).isRequired,
    closeCart: PropTypes.func,
  }

  state = {
    isGiftcardModalOpen: false,
    isUpdatingGiftcard: false,
  }

  handleUpdateGiftcard = unit => {
    this.setState({ isUpdatingGiftcard: true })

    this.props
      .onUpdateGiftcard(this.props.lineItem, unit)
      .then(() => {
        this.setState({ isGiftcardModalOpen: false })
      })
      .always(() => {
        this.setState({ isUpdatingGiftcard: false })
      })
  }

  handleIncrementQuantity = () => {
    if (this.props.onIncrementQuantity && !this.props.isSyncing) {
      this.props.onIncrementQuantity(this.props.lineItem.id)
    }
  }

  handleDecrementQuantity = () => {
    if (this.props.onDecrementQuantity && !this.props.isSyncing) {
      this.props.onDecrementQuantity(this.props.lineItem.id)
    }
  }

  handleRemove = () => {
    if (this.props.onRemove && !this.props.isSyncing) {
      this.props.onRemove(this.props.lineItem.id)
    }
  }

  handleGoToProduct = () => {
    const { lineItem } = this.props
    const destination = `/products/${lineItem.product_permalink}`

    Hub.pub(Event.Checkout.GO_TO_PRODUCT, {
      sku: lineItem.sku,
      product_id: lineItem.product_id,
    })

    routeTo(destination)
    this.props.closeCart()
  }

  renderGiftcardModal() {
    if (!this.state.isGiftcardModalOpen) {
      return null
    }

    return (
      <GiftcardModal
        isLoading={this.setState.isUpdatingGiftcard}
        onSubmit={this.handleUpdateGiftcard}
        onRequestClose={() => this.setState({ isGiftcardModalOpen: false })}
        presetValues={this.props.lineItem.unit_attributes}
      />
    )
  }

  renderQuantityControls() {
    const { lineItem, isSyncing, isFlyoutCart } = this.props

    if (lineItem.is_digital_giftcard) {
      return null
    }

    const quantityClasses = classNames('new-checkout__quantity', {
      'flyout-cart__quantity': isFlyoutCart,
      'new-checkout__quantity--disabled': isSyncing,
    })
    const quantityButtonClasses = classNames('new-checkout__line-item-quantity-button', {
      'flyout-cart__line-item-quantity-button': isFlyoutCart,
    })

    return (
      <div
        className={classNames('new-checkout__line-item-quantity-controls', {
          'flyout-cart__line-item-quantity-controls': isFlyoutCart,
        })}
      >
        <button className={quantityButtonClasses} onClick={this.handleDecrementQuantity}>
          –
        </button>
        <span className={quantityClasses}>{lineItem.quantity}</span>
        <button className={quantityButtonClasses} onClick={this.handleIncrementQuantity}>
          +
        </button>
      </div>
    )
  }

  renderProductImage() {
    const buttonClass = classNames(
      'new-checkout__go-to-product-button',
      'new-checkout__line-item-image',
      { 'flyout-cart__line-item-image': this.props.isFlyoutCart },
    )

    return (
      <button
        onClick={this.handleGoToProduct}
        className={buttonClass}
        style={
          this.props.isFlyoutCart
            ? {
                height: '96px',
                width: '72px',
              }
            : {
                height: this.props.imageHeight,
                width: this.props.imageHeight,
              }
        }
      >
        <img
          src={ImageHelper.imageUrl(this.props.lineItem.image_url, {
            height: this.props.isFlyoutCart ? 96 : this.props.imageHeight,
            width: this.props.isFlyoutCart ? 72 : 'auto',
          })}
          alt={this.props.lineItem.title}
        />
      </button>
    )
  }

  // TODO: make a base line_item component
  renderGiftcardLineItemDetails() {
    const { lineItem, isFlyoutCart } = this.props
    const { abbreviated_size, is_digital_giftcard } = lineItem

    return (
      <div
        className={classNames('new-checkout__line-item-info-details', {
          'flyout-cart__line-item-info-details': isFlyoutCart,
        })}
      >
        <span
          className={classNames('new-checkout__line-item-info', {
            'flyout-cart__line-item-info': isFlyoutCart,
          })}
        >
          {is_digital_giftcard
            ? `Type: ${abbreviated_size} | To: ${lineItem.unit_attributes.recipient_name}`
            : `Type: ${abbreviated_size}`}
        </span>
      </div>
    )
  }

  getSneakerGender = product_permalink => {
    return product_permalink && product_permalink.charAt(0) === 'w' ? 'female' : 'male'
  }

  renderDiscountLabels(discountLabels) {
    let labels = discountLabels
      .map(discountLabel => {
        return discountLabel.label?.replace('Discount - ', '') || discountLabel
      })
      .join(' / ')

    if (labels.length > 30) {
      labels = labels.substring(0, 30) + '…'
    }

    return <span className="flyout-cart__total--discounted__message">({labels})</span>
  }

  renderNonGiftcardLineItemDetails() {
    const { color, product_fits, product_permalink, size, abbreviated_size } = this.props.lineItem
    const isUnisexSneaker = size && size.includes('W') && size.includes('M')
    const visualSize = isUnisexSneaker
      ? getSneakersGenderedSizeName(abbreviated_size, this.getSneakerGender(product_permalink))
      : size

    if (this.props.isFlyoutCart) {
      return (
        <div className="flyout-cart__line-item-info-details">
          <span className="flyout-cart__line-item-info">
            {`${visualSize} | ${color}`}
            {product_fits.length > 0 && ` | ${product_fits[0].display_name}`}
          </span>
        </div>
      )
    }

    return (
      <div className="new-checkout__line-item-info-details">
        <span className="new-checkout__line-item-info">
          {`Size: ${visualSize} `}
          {product_fits.length > 0 && product_fits[0].display_name}
        </span>
        <span className="new-checkout__line-item-info">{`Color: ${color}`}</span>
      </div>
    )
  }

  render() {
    const { isFlyoutCart, lineItem, isSyncing } = this.props

    const isVirtualGiftcardInHoverCart = lineItem.is_digital_giftcard
    const isDiscounted = lineItem.is_promo || lineItem.is_bundle
    const isFinalSale = lineItem.is_final_sale
    const discountedPrice = lineItem.original_total !== lineItem.total
    const discountLabels = isFinalSale ? ['Final Sale'] : lineItem.discount_labels || []

    const totalClass = classNames('new-checkout__total', {
      'flyout-cart__total': isFlyoutCart,
      'flyout-cart__total--discounted': isFlyoutCart && isDiscounted && discountedPrice,
      'new-checkout__total--discounted': isDiscounted && discountedPrice,
    })

    return (
      <Fragment>
        <div
          className={classNames('new-checkout__line-item', {
            'flyout-cart__line-item': isFlyoutCart,
          })}
        >
          <div className="new-checkout__line-item-content-container">
            <div className="new-checkout__product-image-container">{this.renderProductImage()}</div>
            <div className="new-checkout__line-item-info-container">
              <div className="new-checkout__line-item-info-heading">
                <button
                  className="new-checkout__go-to-product-button"
                  onClick={this.handleGoToProduct}
                >
                  <span
                    className={classNames('new-checkout__line-item-info-title', {
                      'flyout-cart__line-item-info-title': isFlyoutCart,
                    })}
                  >
                    {lineItem.title}
                  </span>
                </button>

                <button
                  disabled={isSyncing}
                  className={classNames('new-checkout__line-item-x-button', {
                    'flyout-cart__line-item-x-button': isFlyoutCart,
                  })}
                  onClick={this.handleRemove}
                >
                  {isFlyoutCart ? (
                    <Trash height="16px" width="16px" />
                  ) : (
                    <X height="16px" width="16px" />
                  )}
                </button>
              </div>

              {lineItem.is_giftcard
                ? this.renderGiftcardLineItemDetails()
                : this.renderNonGiftcardLineItemDetails()}

              {isFlyoutCart ? (
                <Fragment>
                  <div className="flyout-cart__line-item-info-sums">
                    <span
                      className={classNames({
                        'flyout-cart__line-item-info-totals': !isVirtualGiftcardInHoverCart,
                      })}
                    >
                      {isDiscounted && discountedPrice && (
                        <span className="flyout-cart__total flyout-cart__total--original">
                          {lineItem.original_total}
                        </span>
                      )}

                      <span className={totalClass}>{lineItem.total}</span>
                      {isDiscounted &&
                        discountLabels.length > 0 &&
                        this.renderDiscountLabels(discountLabels)}
                    </span>
                  </div>
                  {lineItem.is_virtual ? (
                    <div
                      className="new-checkout__line-item-info-edit flyout-cart__gift-card-edit"
                      onClick={() => this.setState({ isGiftcardModalOpen: true })}
                    >
                      Edit card info
                    </div>
                  ) : (
                    this.renderQuantityControls()
                  )}
                </Fragment>
              ) : (
                <div className="new-checkout__line-item-info-sums">
                  {lineItem.is_virtual ? (
                    <div
                      className="new-checkout__line-item-info-edit"
                      onClick={() => this.setState({ isGiftcardModalOpen: true })}
                    >
                      Edit card info
                    </div>
                  ) : (
                    this.renderQuantityControls()
                  )}

                  <span
                    className={classNames({
                      'new-checkout__line-item-info-totals': !isVirtualGiftcardInHoverCart,
                    })}
                  >
                    {isDiscounted && discountedPrice && (
                      <span className="new-checkout__original-total">
                        {lineItem.original_total}
                      </span>
                    )}

                    <span className={totalClass}>{lineItem.total}</span>
                  </span>
                </div>
              )}
            </div>
            {this.renderGiftcardModal()}
          </div>
        </div>
        <LineItemNotifications
          isShippable={!lineItem.is_preorder}
          restockDate={lineItem.restock_date}
          isFlyoutCart={this.props.isFlyoutCart}
        />
      </Fragment>
    )
  }
}

function mapDispatchToProps(dispatch) {
  return {
    onUpdateGiftcard: (lineItem, unit) => dispatch(updateGiftcard(lineItem, unit)),
    closeCart: () => dispatch(closeCart()),
  }
}

export default connect(null, mapDispatchToProps)(LineItem)
